import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../../contexts/AuthContext';
import CustomModuleBuilder from './CustomModuleBuilder';

// Default NREMT Assessment Template with Excel-style structure
const defaultNREMTTemplate = {
  name: '',
  assessmentType: 'Practical Skill',
  description: '',
  timeLimit: '15',
  instructions: '',
  candidateInfo: {
    includeExaminerName: true,
    includeDate: true,
    includeStartTime: true,
    includeEndTime: true,
    includeCandidateName: true,
    includeCandidateID: true
  },
  rows: [
    {
      id: 1,
      type: 'header',
      text: '',
      isMainHeader: true
    },
    {
      id: 2,
      type: 'sub-header',
      text: '',
      hasSectionTotal: false
    },
    {
      id: 3,
      type: 'regular',
      task: '',
      criteria: '',
      critical: false,
      pointsAwarded: 0,
      pointsPossible: 1,
      scoringType: 'pass-fail'
    }
  ],
  evaluation: {
    passFailBased: true,
    totalPointsPossible: 0,
    passingScore: 0,
    allowPartialCredit: false,
    includeCriticalCriteria: true,
    includeOverallPerformance: true,
    includeTimeCompliance: true
  },
  columnWidths: {
    item: 'w-80 md:w-[28rem] min-w-0',     // Item column (was "task") 
    objective: 'w-40 md:w-32 min-w-0',    // Objective column (was "criteria")
    critical: 'w-8 min-w-8',
    score: 'w-12 min-w-12',
    points: 'w-12 min-w-12'
  }
};

// Excel-style inline editing components matching scenario builder
const NREMTHeaderEditor = ({ value, onChange, onConfirm, onCancel, isTextarea = false, placeholder = "Enter text..." }) => (
  <div className="flex items-center justify-center gap-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg p-1 min-w-0">
    {isTextarea ? (
      <textarea 
        className="flex-auto border-none rounded-md resize-none focus:outline-none focus:ring-0 font-poppins font-semibold text-sm capitalize whitespace-pre-wrap break-words min-w-0" 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        placeholder={placeholder}
        rows={2}
        style={{ minHeight: '16px', wordWrap: 'break-word', overflowWrap: 'break-word' }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    ) : (
      <input 
        type="text"
        className="flex-auto border-none rounded-md focus:outline-none focus:ring-0 font-poppins font-semibold text-base capitalize min-w-0" 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        placeholder={placeholder}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    )}
    <div className="flex gap-1 flex-shrink-0">
      <button
        onClick={onConfirm}
        className="bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors p-1.5 w-7 h-7 flex items-center justify-center flex-shrink-0"
        title="Confirm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </button>
      <button
        onClick={onCancel}
        className="bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors p-1.5 w-7 h-7 flex items-center justify-center flex-shrink-0"
        title="Cancel"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
);

const NREMTCellEditor = ({ value, onChange, onConfirm, onCancel, isTextarea = false, placeholder = "Enter text...", customCellData = null }) => (
  <div className="flex items-center gap-1 bg-blue-50 border border-blue-300 rounded transition-opacity duration-300 opacity-100 px-1 py-0.5 w-full min-w-[180px] h-6">
    {isTextarea ? (
      <textarea 
        className={`flex-auto border-none rounded focus:outline-none focus:ring-0 resize-none bg-transparent leading-tight ${customCellData?.italic ? 'italic' : ''} ${customCellData ? `text-${customCellData.textAlign || 'center'}` : 'italic'} ${customCellData ? 'text-[10px]' : 'text-[10px]'}`}
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        placeholder={placeholder}
        rows={1}
        style={{ 
          minHeight: '16px', 
          height: '16px', 
          lineHeight: '16px',
          textAlign: customCellData?.textAlign || 'left'
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    ) : (
      <input 
        type="text"
        className={`flex-auto border-none rounded focus:outline-none focus:ring-0 bg-transparent leading-tight h-4 ${customCellData?.italic ? 'italic' : ''} ${customCellData ? `text-${customCellData.textAlign || 'center'}` : 'italic'} ${customCellData ? 'text-[10px]' : 'text-[10px]'}`}
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        placeholder={placeholder}
        style={{ 
          textAlign: customCellData?.textAlign || 'left'
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    )}
    <div className="flex gap-0.5 flex-shrink-0">
      <button
        onClick={onConfirm}
        className="bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center justify-center flex-shrink-0 p-0.5 w-4 h-4"
        title="Confirm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-2 w-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </button>
      <button
        onClick={onCancel}
        className="bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center justify-center flex-shrink-0 p-0.5 w-4 h-4"
        title="Cancel"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-2 w-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
);

// EditableCell component matching scenario builder functionality
const EditableCell = React.memo(({ 
  value, 
  onChange, 
  onConfirm, 
  onCancel, 
  isHeader = false, 
  isSubHeader = false, 
  isDescription = false,
  rowIndex, 
  field, 
  editingCell, 
  setEditingCell, 
  tempCellValue, 
  setTempCellValue,
  isTextarea = false,
  placeholder = "Click to edit...",
  useItalic = false,
  customCellData = null // New prop to pass custom cell styling data
}) => {
  const cellId = `${rowIndex}-${field}`;
  const isEditing = editingCell === cellId;

  const handleClick = () => {
    if (!isEditing && !editingCell) { // Only allow editing if no other cell is being edited
      setEditingCell(cellId);
      setTempCellValue(value);
    } else if (!isEditing && editingCell) {
      // Create flash overlay effect above the editing row and scroll to it
      const editingRowElement = document.querySelector(`[data-row-index="${editingCell.split('-')[0]}"]`);
      if (editingRowElement) {
        // Scroll to the editing cell smoothly
        editingRowElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        
        // Wait for scroll animation to complete before showing flash
        setTimeout(() => {
          // Create flash overlay
          const flashOverlay = document.createElement('div');
          const rowRect = editingRowElement.getBoundingClientRect();
          
          flashOverlay.style.position = 'fixed';
          flashOverlay.style.top = `${rowRect.top}px`;
          flashOverlay.style.left = `${rowRect.left}px`;
          flashOverlay.style.width = `${rowRect.width}px`;
          flashOverlay.style.height = `${rowRect.height}px`;
          flashOverlay.style.backgroundColor = 'rgba(254, 243, 199, 0.8)';
          flashOverlay.style.border = '1px solid rgb(254, 200, 154)';
          flashOverlay.style.zIndex = '9999';
          flashOverlay.style.pointerEvents = 'none';
          flashOverlay.style.borderRadius = '4px';
          flashOverlay.style.transition = 'opacity 300ms ease-out';
          
          // Add overlay to document body instead of the row to avoid layout changes
          document.body.appendChild(flashOverlay);
          
          // Fade out and remove
          setTimeout(() => {
            flashOverlay.style.opacity = '0';
            setTimeout(() => {
              if (flashOverlay.parentNode) {
                flashOverlay.parentNode.removeChild(flashOverlay);
              }
            }, 300);
          }, 200);
        }, 800); // Wait 800ms for scroll animation to complete
      }
    }
  };

  const handleConfirm = () => {
    onChange(tempCellValue);
    onConfirm();
  };

  const handleCancel = () => {
    setTempCellValue('');
    onCancel();
  };

  if (isEditing) {
    if (isHeader || isSubHeader || isDescription) {
      return (
        <div className="relative z-50 transition-opacity duration-300 opacity-100">
          <NREMTHeaderEditor
            value={tempCellValue}
            onChange={setTempCellValue}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            isTextarea={isDescription || isTextarea}
            placeholder={isDescription ? "Enter description text..." : placeholder}
          />
        </div>
      );
    } else {
      return (
        <div className="relative z-50 transition-opacity duration-300 opacity-100">
          <NREMTCellEditor
            value={tempCellValue}
            onChange={setTempCellValue}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            isTextarea={isTextarea}
            placeholder={placeholder}
            customCellData={customCellData}
          />
        </div>
      );
    }
  }

  return (
    <div 
      onClick={handleClick}
      className={`cursor-pointer h-auto hover:bg-blue-50 transition-colors duration-300 w-full flex items-center px-0.5 py-0.5 ${isDescription ? 'italic text-center justify-center text-[10px]' : ''} ${customCellData ? `justify-${customCellData.textAlign === 'left' ? 'start' : customCellData.textAlign === 'right' ? 'end' : 'center'}` : ''} ${isEditing ? 'opacity-0' : 'opacity-100'} ${editingCell && !isEditing ? 'cursor-not-allowed opacity-50' : ''}`}
      title={editingCell && !isEditing ? "Finish editing current cell first" : "Click to edit"}
    >
      {value ? (
        <span className={`break-words w-full ${customCellData ? 'text-[10px]' : 'text-[10px]'} ${useItalic || customCellData?.italic ? 'italic' : ''} ${customCellData ? `text-${customCellData.textAlign || 'center'}` : ''} ${isDescription && !customCellData ? 'text-[14px] text-center' : ''} ${isHeader ? 'font-poppins font-semibold capitalize text-[11px]' : ''} ${isSubHeader ? 'font-poppins font-semibold capitalize py-0 text-[10px]' : ''} ${isDescription && !customCellData ? 'font-poppins font-normal italic' : ''} ${customCellData?.bold ? 'font-bold' : customCellData ? 'font-medium' : ''}`}>
          {value}
        </span>
      ) : (
        <span className={`text-gray-400 ${useItalic || customCellData?.italic ? 'italic' : ''} break-words w-full ${isDescription && !customCellData ? 'text-center' : ''} ${isHeader ? 'font-poppins' : ''} ${isSubHeader ? 'font-poppins' : ''} ${isDescription ? 'font-poppins' : ''} ${customCellData ? `text-${customCellData.textAlign || 'center'}` : ''} ${customCellData ? 'text-[10px]' : 'text-[10px]'}`}>
          {isDescription ? 'Enter your scenario here...' : placeholder}
        </span>
      )}
    </div>
  );
});
const NREMTAssessmentEditor = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { currentUser, loading: authLoading } = useAuth();
  
  // Use templateId from params (matches route definition)
  const id = templateId;
  
  // State management similar to scenario builder
  const [template, setTemplate] = useState(defaultNREMTTemplate);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [tempCellValue, setTempCellValue] = useState('');
  
  // Custom module state
  const [showCustomModuleBuilder, setShowCustomModuleBuilder] = useState(false);
  const [editingCustomModule, setEditingCustomModule] = useState(null);
  
  // Mobile sidebar state for end columns
  const [showEndColumns, setShowEndColumns] = useState(true);
  const [printMode, setPrintMode] = useState(false);
  const [borderBottomOnly, setBorderBottomOnly] = useState(false);
  const [hideActionColumn, setHideActionColumn] = useState(false);
  const [hideDragColumns, setHideDragColumns] = useState(false);
  const [showSelectionTool, setShowSelectionTool] = useState(false);
  const [columnWidthAdjustment, setColumnWidthAdjustment] = useState(0); // -100 to +100
  const [scoreColumnWidth, setScoreColumnWidth] = useState(0); // Score column width adjustment
  const [pointsColumnWidth, setPointsColumnWidth] = useState(0); // Points column width adjustment
  const [containerWidth, setContainerWidth] = useState(75); // Container width as percentage of viewport (75vw default)
  const [showWidthSettings, setShowWidthSettings] = useState(false); // Show width settings panel
  const [selectionRect, setSelectionRect] = useState(null);
  const [showPageBorder, setShowPageBorder] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isActionsColumnHovered, setIsActionsColumnHovered] = useState(false);
  const [isDragColumnHovered, setIsDragColumnHovered] = useState(false);
  
  // Drag and drop state for adding rows
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDraggingNewRow, setIsDraggingNewRow] = useState(false);

  // Auto-save state management
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const autoSaveTimeoutRef = useRef(null);
  const messageTimeoutRef = useRef(null);
  const lastTemplateRef = useRef(null);
  const saveQueueRef = useRef(false);

  // Helper functions for Firestore data management
  const sanitizeForFirestore = useCallback((obj) => {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForFirestore(item)).filter(item => item !== undefined);
    }
    
    if (typeof obj === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          sanitized[key] = sanitizeForFirestore(value);
        }
      }
      return sanitized;
    }
    
    return obj;
  }, []);

  const flattenCellsForFirestore = useCallback((rows) => {
    return rows.map(row => {
      // Ensure all required fields have default values
      const sanitizedRow = {
        id: row.id,
        type: row.type || 'regular',
        text: row.text || '',
        task: row.task || '',
        criteria: row.criteria || '',
        critical: row.critical || false,
        pointsAwarded: row.pointsAwarded || 0,
        pointsPossible: row.pointsPossible || 1,
        scoreAwarded: row.scoreAwarded || '',
        scoringType: row.scoringType || 'pass-fail',
        isMainHeader: row.isMainHeader || false,
        hasSectionTotal: row.hasSectionTotal || false,
        itemText: row.itemText || '',
        rows: row.rows || 2,
        columns: row.columns || 2
      };

      if (row.type === 'custom' && row.cells) {
        return {
          ...sanitizedRow,
          cells: row.cells.flat(), // Flatten 2D array to 1D
          cellsRows: row.rows || 2, // Store original dimensions
          cellsColumns: row.columns || 2
        };
      }
      return sanitizedRow;
    });
  }, []);

  // Sync column width states with template when it loads
  useEffect(() => {
    if (template.columnWidths) {
      setColumnWidthAdjustment(template.columnWidths.itemAdjustment || 0);
      setScoreColumnWidth(template.columnWidths.scoreAdjustment || 0);
      setPointsColumnWidth(template.columnWidths.pointsAdjustment || 0);
    }
  }, [template.columnWidths]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setShowEndColumns(false); // Auto-hide on mobile
      } else {
        setShowEndColumns(true); // Auto-show on desktop
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load template data
  useEffect(() => {
    const loadTemplate = async () => {
      if (!id || id === 'new') {
        setTemplate(defaultNREMTTemplate);
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'nremtAssessments', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          setTemplate({
            ...data,
            rows: reconstructCellsFromFirestore(data.rows || []), // Reconstruct cells after loading
            columnWidths: {
              ...defaultNREMTTemplate.columnWidths,
              ...(data.columnWidths || {})
            }
          });
          
        } else {
          setTemplate(defaultNREMTTemplate);
        }
      } catch (error) {
        console.error('Error loading template:', error);
        setMessage('Failed to load assessment template');
      }
      setLoading(false);
    };

    loadTemplate();
  }, [id]);

  // Auto-save functionality with debouncing and optimization
  const autoSave = useCallback(async () => {
    if (!autoSaveEnabled || !hasUnsavedChanges || !template.name.trim() || !currentUser?.uid || saveQueueRef.current) {
      return;
    }

    // Prevent multiple simultaneous saves
    saveQueueRef.current = true;
    
    try {
      const templateData = sanitizeForFirestore({
        ...template,
        rows: flattenCellsForFirestore(template.rows),
        columnWidths: {
          ...template.columnWidths,
          itemAdjustment: columnWidthAdjustment,
          scoreAdjustment: scoreColumnWidth,
          pointsAdjustment: pointsColumnWidth
        },
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        autoSaved: true // Flag to indicate auto-save vs manual save
      });

      if (id && id !== 'new') {
        await updateDoc(doc(db, 'nremtAssessments', id), templateData);
      } else {
        const docRef = await addDoc(collection(db, 'nremtAssessments'), {
          ...templateData,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp()
        });
        // Update the URL without navigating away
        window.history.replaceState(null, '', `/instructor/nremt-builder/edit/${docRef.id}`);
      }

      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      lastTemplateRef.current = JSON.stringify(template);
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't show error to user for auto-save failures, just log them
    } finally {
      saveQueueRef.current = false;
    }
  }, [template, hasUnsavedChanges, autoSaveEnabled, currentUser?.uid, id, sanitizeForFirestore, flattenCellsForFirestore, columnWidthAdjustment, scoreColumnWidth, pointsColumnWidth]);

  // Debounced auto-save effect - saves 2 seconds after last change
  useEffect(() => {
    if (!hasUnsavedChanges || !autoSaveEnabled) return;

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 2000); // 2 second debounce

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, autoSave, autoSaveEnabled]);

  // Track column width changes for auto-save
  useEffect(() => {
    // Mark as having unsaved changes when column widths change
    setHasUnsavedChanges(true);
  }, [columnWidthAdjustment, scoreColumnWidth, pointsColumnWidth]);

  // Track template changes for auto-save
  useEffect(() => {
    if (!lastTemplateRef.current) {
      lastTemplateRef.current = JSON.stringify(template);
      return;
    }

    const currentTemplateString = JSON.stringify(template);
    if (currentTemplateString !== lastTemplateRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [template]);

  // Save on page unload if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        const message = 'You have unsaved changes. Do you want to leave without saving?';
        e.returnValue = message;
        return message;
      }
    };

    const handleUnload = () => {
      if (hasUnsavedChanges && autoSaveEnabled) {
        // Use sendBeacon for reliable save on page unload
        const templateData = sanitizeForFirestore({
          ...template,
          rows: flattenCellsForFirestore(template.rows),
          updatedBy: currentUser?.uid,
          updatedAt: new Date().toISOString(),
          autoSaved: true
        });
        
        // Note: This is a best-effort save. Modern browsers may limit this.
        navigator.sendBeacon('/api/save-assessment', JSON.stringify(templateData));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [hasUnsavedChanges, template, currentUser?.uid, autoSaveEnabled, sanitizeForFirestore, flattenCellsForFirestore]);

  // Column width helper functions - using inline styles for dynamic adjustment
  const getItemWidth = () => {
    const baseWidth = 320; // base width in pixels
    return baseWidth + (columnWidthAdjustment * 2);
  };
  
  const getObjectiveWidth = () => {
    const baseWidth = 180; // Increased base width for objective column
    return Math.max(100, baseWidth - (columnWidthAdjustment * 2));
  };

  // Class-based functions for backward compatibility (for cells that don't use the slider)
  const getItemWidthClass = () => template.columnWidths?.item || 'w-80 min-w-80';
  const getObjectiveWidthClass = () => template.columnWidths?.objective || 'w-40 min-w-40';
  
  // Manual width adjustments for other columns - you can change these values
  const getCriticalWidthClass = () => template.columnWidths?.critical || 'w-6 min-w-6'; // Smaller than original w-8
  const getScoreWidthClass = () => template.columnWidths?.score || 'w-6 min-w-6'; // Much smaller score column
  const getPointsWidthClass = () => template.columnWidths?.points || 'w-8 min-w-8'; // Smaller than original w-12
  const getActionWidthClass = () => template.columnWidths?.action || 'w-12 min-w-12'; // Smaller action column

  // Dynamic score column width function
  const getScoreWidth = () => {
    const baseWidth = 24; // base width in pixels (w-6)
    return Math.max(16, baseWidth + scoreColumnWidth); // minimum 16px
  };

  // Dynamic points column width function
  const getPointsWidth = () => {
    const baseWidth = 32; // base width in pixels (w-8)
    return Math.max(20, baseWidth + pointsColumnWidth); // minimum 20px
  };

  // Custom module responsive grid helper
  const getCustomModuleGridSize = (moduleName, columns, isMobile = false) => {
    const moduleNameLower = (moduleName || '').toLowerCase();
    
    // Apply column width adjustment factor
    const widthMultiplier = 1 + (columnWidthAdjustment / 100);
    
    // Special responsive rules for specific modules
    if (moduleNameLower.includes('loc') || 
        moduleNameLower.includes('mental status') || 
        moduleNameLower.includes('dcapbtls') ||
        moduleNameLower.includes('dcap-btls') ||
        moduleNameLower.includes('dcap btls')) {
      // More aggressive compression for these modules
      if (isMobile) {
        const baseSize = columns > 4 ? 40 : columns > 2 ? 60 : 80;
        return `${Math.max(20, baseSize * widthMultiplier)}px`;
      } else {
        const baseSize = columns > 4 ? 50 : columns > 2 ? 70 : 100;
        return `${Math.max(25, baseSize * widthMultiplier)}px`;
      }
    }
    
    // Default responsive behavior for other modules - much more aggressive for high column counts
    if (isMobile) {
      const baseSize = columns >= 10 ? 30 : columns >= 8 ? 35 : columns > 6 ? 50 : columns > 4 ? 100 : 160;
      return `${Math.max(20, baseSize * widthMultiplier)}px`;
    } else {
      const baseSize = columns >= 10 ? 35 : columns >= 8 ? 40 : columns > 6 ? 60 : columns > 4 ? 120 : 160;
      return `${Math.max(25, baseSize * widthMultiplier)}px`;
    }
  };

  // Get padding class based on column count for custom modules
  const getCustomModulePadding = (columns) => {
    // Adjust padding based on column width adjustment
    const isNarrower = columnWidthAdjustment < -20; // If columns are significantly narrower
    
    if (columns >= 10) {
      return isNarrower ? 'px-0 py-0.5' : 'px-0.5 py-0.5'; // Minimal padding for 10+ columns
    } else if (columns >= 8) {
      return isNarrower ? 'px-0.5 py-0.5' : 'px-1 py-0.5'; // Very small padding for 8-9 columns
    } else if (columns >= 6) {
      return isNarrower ? 'px-0.5 py-1' : 'px-1 py-1'; // Small padding for 6-7 columns
    } else {
      return isNarrower ? 'px-1 py-1' : 'px-2 py-1'; // Normal padding for fewer columns
    }
  };

  // Get checkbox size class based on column count and width adjustment
  const getCustomModuleCheckboxSize = (columns) => {
    // Adjust checkbox size based on column width adjustment
    const isNarrower = columnWidthAdjustment < -20; // If columns are significantly narrower
    
    if (columns >= 10) {
      return isNarrower ? 'w-3 h-3' : 'w-4 h-4'; // Smaller checkboxes for 10+ columns
    } else if (columns >= 8) {
      return isNarrower ? 'w-3 h-3' : 'w-4 h-4'; // Small checkboxes for 8-9 columns
    } else if (columns >= 6) {
      return isNarrower ? 'w-4 h-4' : 'w-5 h-5'; // Medium checkboxes for 6-7 columns
    } else {
      return 'w-5 h-5'; // Normal size checkboxes for fewer columns
    }
  };

  // Get text size class based on column count for custom modules
  const getCustomModuleTextSize = (columns) => {
    // Adjust text size based on column width adjustment
    const isNarrower = columnWidthAdjustment < -20; // If columns are significantly narrower
    
    if (columns >= 10) {
      return isNarrower ? 'text-[8px]' : 'text-[9px]'; // Extra extra small text for 10+ columns
    } else if (columns >= 8) {
      return isNarrower ? 'text-[9px]' : 'text-[10px]'; // Extra small text for 8-9 columns
    } else if (columns >= 6) {
      return isNarrower ? 'text-[9px]' : 'text-[10px]'; // Small text for 6-7 columns
    } else {
      return isNarrower ? 'text-[9px]' : 'text-[10px]'; // Normal small text for fewer columns
    }
  };

  // Calculate total points for assessment
  const calculateTotalPoints = () => {
    return template.rows.reduce((total, row) => {
      if (row.type === 'regular') {
        return total + (row.pointsPossible || 1);
      } else if (row.type === 'custom') {
        return total + (row.pointsPossible || 1);
      }
      return total;
    }, 0);
  };

  // Generate unique ID for new rows
  const generateUniqueId = () => {
    // Use crypto.randomUUID if available (modern browsers), fallback to custom UUID
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  // Row management functions
  const addRow = (type = 'regular', insertIndex = null) => {
    const newRow = {
      id: generateUniqueId(), // Proper UUID
      type,
      task: '',
      criteria: '',
      critical: false,
      pointsAwarded: 0,
      pointsPossible: 1,
      scoreAwarded: '',
      scoringType: 'pass-fail'
    };

    if (type === 'header') {
      newRow.text = '';
      newRow.isMainHeader = false;
    } else if (type === 'sub-header') {
      newRow.text = '';
      newRow.hasSectionTotal = false;
    } else if (type === 'description') {
      newRow.text = '';
    }

    setTemplate(prev => {
      const newRows = [...prev.rows];
      if (insertIndex !== null && insertIndex >= 0 && insertIndex <= newRows.length) {
        newRows.splice(insertIndex, 0, newRow);
      } else {
        newRows.push(newRow);
      }
      return {
        ...prev,
        rows: newRows
      };
    });
  };

  const addDescription = (insertIndex = null) => {
    const newRow = { 
      id: generateUniqueId(), // Proper UUID
      type: 'description', 
      text: '', 
      task: '', 
      criteria: '', 
      critical: false, 
      scoreAwarded: '', 
      pointsPossible: 1
    };
    
    setTemplate(prev => {
      const newRows = [...prev.rows];
      if (insertIndex !== null && insertIndex >= 0 && insertIndex <= newRows.length) {
        newRows.splice(insertIndex, 0, newRow);
      } else {
        newRows.push(newRow);
      }
      return {
        ...prev,
        rows: newRows
      };
    });
  };

  // Custom module handler
  const handleCustomModuleSave = (customRow) => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, customRow]
    }));
  };

  // Custom module editing handlers
  const editCustomModule = (row, index) => {
    setEditingCustomModule({ ...row, originalIndex: index });
    setShowCustomModuleBuilder(true);
  };

  const updateCustomModule = (updatedRow) => {
    if (editingCustomModule && editingCustomModule.originalIndex !== undefined) {
      setTemplate(prev => ({
        ...prev,
        rows: prev.rows.map((row, i) => 
          i === editingCustomModule.originalIndex ? updatedRow : row
        )
      }));
    }
    setEditingCustomModule(null);
  };

  const removeRow = (index) => {
    setTemplate(prev => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== index)
    }));
  };

  const handleRowChange = useCallback((index, field, value) => {
    setTemplate(prev => ({
      ...prev,
      rows: prev.rows.map((row, i) => 
        i === index ? { ...row, [field]: value } : row
      )
    }));
  }, []);

  // Cell editing functions
  const handleCellConfirm = useCallback(() => {
    setEditingCell(null);
    setTempCellValue('');
  }, []);

  const handleCellCancel = useCallback(() => {
    setEditingCell(null);
    setTempCellValue('');
  }, []);

  // Drag and drop functionality
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(template.rows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTemplate(prev => ({
      ...prev,
      rows: items
    }));
  };

  const reconstructCellsFromFirestore = (rows) => {
    return rows.map(row => {
      if (row.type === 'custom' && row.cells && Array.isArray(row.cells) && row.cellsRows && row.cellsColumns) {
        // Reconstruct 2D array from flattened array
        const cells2D = [];
        const flatCells = row.cells;
        const rows = row.cellsRows;
        const cols = row.cellsColumns;
        
        for (let i = 0; i < rows; i++) {
          const rowCells = [];
          for (let j = 0; j < cols; j++) {
            const index = i * cols + j;
            const cell = flatCells[index] || { text: '', type: 'checkbox' };
            // Ensure all properties exist with defaults
            rowCells.push({
              text: cell.text || '',
              type: cell.type || 'checkbox',
              bold: cell.bold || false,
              italic: cell.italic || false,
              checkboxPosition: cell.checkboxPosition || 'bottom',
              editable: cell.editable || false,
              checked: cell.checked || false
            });
          }
          cells2D.push(rowCells);
        }
        
        return {
          ...row,
          cells: cells2D,
          rows: row.cellsRows,
          columns: row.cellsColumns,
          pointsPossible: row.pointsPossible || 1 // Ensure custom modules have pointsPossible
        };
      }
      return {
        ...row,
        pointsPossible: row.pointsPossible || (row.type === 'regular' || row.type === 'custom' ? 1 : undefined) // Add pointsPossible to all scoreable rows
      };
    });
  };

  // Manual save functionality (enhanced to work with auto-save)
  const saveTemplate = async () => {
    if (!template.name.trim()) {
      setMessage('Please enter an assessment name');
      return;
    }

    if (!currentUser?.uid) {
      setMessage('Authentication required. Please log in again.');
      return;
    }

    // Prevent multiple simultaneous saves
    if (saveQueueRef.current) {
      setMessage('Save already in progress...');
      return;
    }
    
    setSaving(true);
    saveQueueRef.current = true;

    try {
      const templateData = sanitizeForFirestore({
        ...template,
        rows: flattenCellsForFirestore(template.rows),
        columnWidths: {
          ...template.columnWidths,
          itemAdjustment: columnWidthAdjustment,
          scoreAdjustment: scoreColumnWidth,
          pointsAdjustment: pointsColumnWidth
        },
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        autoSaved: false // Flag to indicate manual save
      });

      if (id && id !== 'new') {
        await updateDoc(doc(db, 'nremtAssessments', id), templateData);
        setMessage('Assessment updated successfully!');
        
        // Auto-clear message after 3 seconds
        if (messageTimeoutRef.current) {
          clearTimeout(messageTimeoutRef.current);
        }
        messageTimeoutRef.current = setTimeout(() => {
          setMessage('');
        }, 3000);
        
      } else {
        const docRef = await addDoc(collection(db, 'nremtAssessments'), {
          ...templateData,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp()
        });
        // Update the URL without navigating away
        window.history.replaceState(null, '', `/instructor/nremt-builder/edit/${docRef.id}`);
        setMessage('Assessment created successfully!');
        
        // Auto-clear message after 3 seconds
        if (messageTimeoutRef.current) {
          clearTimeout(messageTimeoutRef.current);
        }
        messageTimeoutRef.current = setTimeout(() => {
          setMessage('');
        }, 3000);
      }

      // Update auto-save state
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      lastTemplateRef.current = JSON.stringify(template);

    } catch (error) {
      console.error('Error saving template:', error);
      setMessage('Failed to save assessment: ' + error.message);
    } finally {
      setSaving(false);
      saveQueueRef.current = false;
    }
  };

  // Row management functions

  const cancelSelection = () => {
    setShowSelectionTool(false);
    setSelectionRect(null);
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <>   
      <style>
        {/* COMPACT TABLE STYLES */}
        {`
          /* Make table rows and cells more compact */
          table td, table th {
            padding: 0.125rem !important;
            line-height: 1.1 !important;
            vertical-align: top !important;
          }
          
          /* Reduce all row heights */
          table tr {
            height: auto !important;
            min-height: 16px !important;
          }
          
          /* Reduce header row heights */
          .header-row {
            height: auto !important;
            min-height: 18px !important;
            padding: 0.125rem !important;
          }
          
          /* Make regular rows more compact */
          tbody tr {
            height: auto !important;
            min-height: 16px !important;
          }
          
          /* Compact buttons and controls */
          .action-column button {
            padding: 0.125rem !important;
            margin: 0.05rem !important;
            width: 16px !important;
            height: 16px !important;
          }
          
          /* Reduce spacing in editable cells */
          .editable-cell {
            min-height: 14px !important;
            line-height: 1.0 !important;
          }
          
          /* Compact custom module cells */
          .custom-module-cell {
            padding: 0.1rem !important;
            line-height: 1.0 !important;
            min-height: 14px !important;
          }
          
          /* Reduce input and textarea heights */
          input[type="text"], textarea {
            min-height: 14px !important;
            padding: 0.125rem !important;
          }
          
          /* Make drag handles smaller */
          .drag-handle {
            width: 12px !important;
            height: 12px !important;
          }
        `}
        
        {/* CLEAN MODE STYLES */}
        {printMode && `
          .print-mode table,
          .print-mode th,
          .print-mode td,
          .print-mode div,
          .print-mode input,
          .print-mode span {
            border: none !important;
            box-shadow: none !important;
          }
          
          /* Remove all border classes */
          .print-mode .border,
          .print-mode .border-gray-300,
          .print-mode .border-gray-400,
          .print-mode .border-blue-200,
          .print-mode .border-blue-300,
          .print-mode .border-blue-400,
          .print-mode .border-indigo-300 {
            border: none !important;
          }
          
          /* Keep only score/points column borders for writing */
          .print-mode .score-column,
          .print-mode .points-column {
            border: 1px solid #d1d5db !important;
          }
          
          /* Keep assessment total score line */
          .print-mode .assessment-total-score {
            border-bottom: 1px solid #9ca3af !important;
          }
        `}
        
        {/* BORDER BOTTOM ONLY MODE STYLES */}
        {borderBottomOnly && !printMode && `
          .border-bottom-only table,
          .border-bottom-only th,
          .border-bottom-only td,
          .border-bottom-only div,
          .border-bottom-only input,
          .border-bottom-only span {
            border: none !important;
            box-shadow: none !important;
          }
          
          /* Remove all border classes */
          .border-bottom-only .border,
          .border-bottom-only .border-gray-300,
          .border-bottom-only .border-gray-400,
          .border-bottom-only .border-blue-200,
          .border-bottom-only .border-blue-300,
          .border-bottom-only .border-blue-400,
          .border-bottom-only .border-blue-500 {
            border: none !important;
          }
          
          /* Apply bottom border to content cells only (not headers) */
          .border-bottom-only tbody td:not(.opacity-0):not(.score-column):not(.points-column) {
            border-bottom: 1px solid #d1d5db !important;
          }
          
          /* Keep score/points column borders */
          .border-bottom-only .score-column,
          .border-bottom-only .points-column {
            border: 1px solid #d1d5db !important;
          }
          
          /* Remove backgrounds */
          .border-bottom-only .bg-blue-50,
          .border-bottom-only .bg-amber-50,
          .border-bottom-only .bg-purple-100,
          .border-bottom-only .bg-indigo-100,
          .border-bottom-only .shadow-lg,
          .border-bottom-only .shadow-md {
            background: white !important;
            box-shadow: none !important;
          }
        `}
        
        {/* PRINT STYLES */}
        {`
          @media print {
            /* Hide ALL non-printable elements including navigation */
            .no-print,
            nav,
            .navbar,
            .nav,
            header,
            .header,
            .sidebar,
            .menu,
            .toolbar,
            .controls,
            .fixed,
            .sticky,
            button:not(.print-only),
            .btn:not(.print-only),
            .floating,
            .overlay,
            .modal,
            .dropdown,
            .tooltip,
            .popover,
            .alert,
            .notification,
            .breadcrumb,
            .pagination,
            .tabs,
            .tab-content:not(.print-content),
            .footer,
            .aside,
            .widget,
            .ad,
            .advertisement,
            .social,
            .share,
            .comments,
            .related,
            .recommended { 
              display: none !important; 
              visibility: hidden !important;
            }
            
            /* Reset body and html for print */
            html, body { 
              background: white !important; 
              color: black !important;
              font-family: Arial, sans-serif !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            /* Page settings */
            @page {
              size: 8.5in 11in;
              margin: 0.5in 0.75in;
            }
            
            table, th, td {
              border: none !important;
              border-collapse: collapse !important;
            }
            
            .bg-blue-50, .bg-amber-50, .bg-white {
              background: white !important;
            }
            
            .shadow-lg { box-shadow: none !important; }
            
            /* Keep score/points borders in print */
            .score-column, .points-column {
              border: 1px solid #d1d5db !important;
            }
            
            .assessment-total-score {
              border-bottom: 1px solid #9ca3af !important;
            }
            
            /* Hide React dev tools and other browser elements */
            #react-devtools-root,
            [data-reactroot] > div:not(.printable-content),
            .react-dev-overlay,
            .webpack-dev-server-overlay {
              display: none !important;
            }
          }
        `}
      </style>   
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={`min-h-screen white relative pb-32 ${printMode ? 'print-mode' : ''} ${borderBottomOnly && !printMode ? 'border-bottom-only' : ''}`}>{/* Increased pb-32 for larger sticky taskbar space */}
{/* Header */}
          <div className="bg-white shadow-none border-b no-print">
            <div className="max-w-6xl mx-auto px-4 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate('/nremt-dashboard')}
                    className={`text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2 transition-opacity duration-300 ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={editingCell}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Back to NREMT Dashboard
                  </button>
                  <h1 className="text-2xl font-bold text-gray-900">MXC Scenario Builder</h1>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      saveTemplate();
                    }}
                    disabled={saving || editingCell}
                    className={`bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-1.5 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                        </svg>
                        Save Assessment
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setPrintMode(!printMode)}
                    disabled={editingCell}
                    className={`w-8 h-8 rounded-lg font-medium transition-all duration-300 flex items-center justify-center ${
                      printMode 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    } ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={printMode ? "Exit clean mode (hide borders)" : "Enter clean mode (hide borders)"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => setBorderBottomOnly(!borderBottomOnly)}
                    disabled={editingCell}
                    className={`w-8 h-8 rounded-lg font-medium transition-all duration-300 flex items-center justify-center ${
                      borderBottomOnly 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    } ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={borderBottomOnly ? "Show all borders" : "Show only bottom borders"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 19h18v2H3v-2zM3 3h18v2H3V3zm0 4h18v2H3V7zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
                    </svg>
                  </button>

                  <button
                    onClick={() => setShowPageBorder(!showPageBorder)}
                    disabled={editingCell}
                    className={`w-8 h-8 rounded-lg font-medium transition-all duration-300 flex items-center justify-center ${
                      showPageBorder 
                        ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    } ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={showPageBorder ? "Hide page boundary" : "Show page boundary (8.5x11 print area)"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h2v2H7zM7 15h2v2H7zM15 7h2v2h-2zM15 15h2v2h-2z"/>
                    </svg>
                  </button>

                  <button
                    onClick={() => {
                      setHideActionColumn(!hideActionColumn);
                      setHideDragColumns(!hideDragColumns);
                    }}
                    disabled={editingCell}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${
                      hideActionColumn 
                        ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    } ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={hideActionColumn ? "Show utility columns" : "Hide utility columns"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                    </svg>
                    {hideActionColumn ? '👁️ Utility' : '🙈 Utility'}
                  </button>

                  <button
                    onClick={() => window.print()}
                    disabled={editingCell}
                    className={`bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-4 py-1.5 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${editingCell ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                    </svg>
                    Print
                  </button>
                </div>
              </div>
              
{/* Basic Assessment Info */}
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Assessment Name</label>
                  <div className="w-full border border-gray-300 rounded-lg px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 min-h-[32px]">
                    <EditableCell
                      value={template.name}
                      onChange={(value) => setTemplate(prev => ({ ...prev, name: value }))}
                      onConfirm={handleCellConfirm}
                      onCancel={handleCellCancel}
                      isHeader={false}
                      rowIndex="assessment"
                      field="name"
                      editingCell={editingCell}
                      setEditingCell={setEditingCell}
                      tempCellValue={tempCellValue}
                      setTempCellValue={setTempCellValue}
                      placeholder="Enter assessment name..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Assessment Type</label>
                  <select
                    value={template.assessmentType}
                    onChange={(e) => setTemplate(prev => ({ ...prev, assessmentType: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Practical Skill">Practical Skill</option>
                    <option value="Patient Assessment">Patient Assessment</option>
                    <option value="BLS Assessment">BLS Assessment</option>
                    <option value="Trauma Assessment">Trauma Assessment</option>
                    <option value="Medical Assessment">Medical Assessment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Time Limit (minutes)</label>
                  <div className="w-full border border-gray-300 rounded-lg px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 min-h-[32px]">
                    <EditableCell
                      value={template.timeLimit}
                      onChange={(value) => setTemplate(prev => ({ ...prev, timeLimit: value }))}
                      onConfirm={handleCellConfirm}
                      onCancel={handleCellCancel}
                      isHeader={false}
                      rowIndex="assessment"
                      field="timeLimit"
                      editingCell={editingCell}
                      setEditingCell={setEditingCell}
                      tempCellValue={tempCellValue}
                      setTempCellValue={setTempCellValue}
                      placeholder="15"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

{/* Add Row Buttons - Now as Sticky Bottom Taskbar */}
          <div className={`fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 transition-opacity duration-300 no-print ${editingCell ? 'opacity-50' : ''}`}>
            
            {/* Success message - positioned absolutely to avoid layout displacement */}
            {message && (
              <div className="absolute top-[-60px] left-4 flex items-center gap-2 px-3 py-2 bg-green-100 border border-green-400 text-green-700 rounded-md text-sm font-medium animate-in fade-in duration-300 shadow-lg z-10">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {message}
              </div>
            )}
            
            <div className="max-w-6xl mx-auto px-3 py-2 md:px-6">
              <div className="flex items-center justify-center relative">
                {/* Center - Add Row buttons */}
                <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
                  <span className="text-xs md:text-sm font-semibold text-gray-700 mr-1 md:mr-2">Add Row:</span>
                  <button
                    draggable={!editingCell}
                    onDragStart={(e) => {
                      if (!editingCell) {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'add-row', rowType: 'header' }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setIsDraggingNewRow(true);
                      }
                    }}
                    onDragEnd={() => {
                      setIsDraggingNewRow(false);
                      setDragOverIndex(null);
                    }}
                    onClick={() => !editingCell && addRow('header')}
                    disabled={editingCell}
                    className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-1.5 py-1.5 shadow-md border border-indigo-200 rounded text-xs font-medium transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing hover:scale-105 min-w-[70px] justify-center"
                    title="Drag to position or click to add at end"
                  >
                    <span className="font-bold text-sm">+</span> 
                    <span>Header</span>
                  </button>
                  <button
                    draggable={!editingCell}
                    onDragStart={(e) => {
                      if (!editingCell) {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'add-row', rowType: 'sub-header' }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setIsDraggingNewRow(true);
                      }
                    }}
                    onDragEnd={() => {
                      setIsDraggingNewRow(false);
                      setDragOverIndex(null);
                    }}
                    onClick={() => !editingCell && addRow('sub-header')}
                    disabled={editingCell}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-800 px-1.5 py-1.5 shadow-md border border-purple-200 rounded text-xs font-medium transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing hover:scale-105 min-w-[80px] justify-center"
                    title="Drag to position or click to add at end"
                  >
                    <span className="font-bold text-sm">SH</span> 
                    <span>Sub-Header</span>
                  </button>
                  <button
                    draggable={!editingCell}
                    onDragStart={(e) => {
                      if (!editingCell) {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'add-row', rowType: 'description' }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setIsDraggingNewRow(true);
                      }
                    }}
                    onDragEnd={() => {
                      setIsDraggingNewRow(false);
                      setDragOverIndex(null);
                    }}
                    onClick={() => !editingCell && addDescription()}
                    disabled={editingCell}
                    className="bg-amber-100 hover:bg-amber-200 text-amber-800 px-1.5 py-1.5 shadow-md border border-amber-200 rounded text-xs font-medium transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing hover:scale-105 min-w-[75px] justify-center"
                    title="Drag to position or click to add at end"
                  >
                    <span className="font-bold text-sm">D</span> 
                    <span>Description</span>
                  </button>
                  <button
                    draggable={!editingCell}
                    onDragStart={(e) => {
                      if (!editingCell) {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'add-row', rowType: 'regular' }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setIsDraggingNewRow(true);
                      }
                    }}
                    onDragEnd={() => {
                      setIsDraggingNewRow(false);
                      setDragOverIndex(null);
                    }}
                    onClick={() => !editingCell && addRow('regular')}
                    disabled={editingCell}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-1.5 py-1.5 shadow-md border border-blue-200 rounded text-xs font-medium transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing hover:scale-105 min-w-[70px] justify-center"
                    title="Drag to position or click to add at end"
                  >
                    <span className="font-bold text-sm">+</span> 
                    <span>Task Row</span>
                  </button>
                  <button
                    draggable={!editingCell}
                    onDragStart={(e) => {
                      if (!editingCell) {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'add-row', rowType: 'custom' }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setIsDraggingNewRow(true);
                      }
                    }}
                    onDragEnd={() => {
                      setIsDraggingNewRow(false);
                      setDragOverIndex(null);
                    }}
                    onClick={() => !editingCell && setShowCustomModuleBuilder(true)}
                    disabled={editingCell}
                    className="bg-green-100 hover:bg-green-200 text-green-800 px-1.5 py-1.5 shadow-md border border-green-200 rounded text-xs font-medium transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing hover:scale-105 min-w-[90px] justify-center"
                    title="Drag to position or click to add at end"
                  >
                    <span className="font-bold text-sm">📋</span> 
                    <span>Custom Module</span>
                  </button>
                </div>
                
                {/* Right side - Auto-save controls positioned absolutely */}
                <div className="flex justify-right pl-10 gap-2">
                  {/* Auto-save status indicator */}
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        autoSaveEnabled 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={autoSaveEnabled ? "Auto-save: ON" : "Auto-save: OFF"}
                    >
                      <div className={`w-2 h-2 rounded-full ${autoSaveEnabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      <span className="hidden sm:inline">Auto-save</span>
                    </button>
                    
                    {hasUnsavedChanges && (
                      <span className="text-amber-600 text-xs font-medium flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        <span className="hidden sm:inline">Unsaved</span>
                      </span>
                    )}
                    
                    {lastSaved && !hasUnsavedChanges && (
                      <span className="text-green-600 text-xs font-medium flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="hidden sm:inline">{lastSaved.toLocaleTimeString()}</span>
                      </span>
                    )}
                  </div>
                  
                  {isMobile && (
                    <button
                      onClick={() => setShowEndColumns(!showEndColumns)}
                      disabled={editingCell}
                      className={`px-2 py-2 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg ${
                        showEndColumns 
                          ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-200' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                      title={showEndColumns ? "Hide Score/Actions" : "Show Score/Actions"}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="hidden sm:inline">{showEndColumns ? 'Hide' : 'Show'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

{/* 📄 MASTER CONTAINER CONTROLS - For condensed UI and print optimization */}
{/* Excel-style Table */}
{/* 🎯 OUTER CONTAINER: Responsive width with proper constraints */}
          <div className="flex justify-center px-4">

{/* 🎯 SCALE CONTAINER: Controlled width with responsive behavior */}
            <div className="bg-white rounded-lg shadow-lg shadow-gray-300 w-full" style={{ maxWidth: `${containerWidth}vw` }}>
              <div className="bg-white rounded-lg">

{/* 🎯 INNER PADDING: p-4 (1rem/16px all sides) - reduced for more condensed layout */}
                <div className="p-4 rounded-lg mt-3 overflow-x-auto">
                  {/* Printable Assessment Container */}
                  <div 
                    id="printable-assessment"
                    className="printable-content"
                    style={{ position: 'relative' }}
                  >
                    {/* Page Border Overlay - 8.5" x 11" aspect ratio with print margins */}
                    {showPageBorder && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '0',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '8.5in',
                          height: '11in',
                          border: '2px dashed #f59e0b',
                          backgroundColor: 'rgba(245, 158, 11, 0.05)',
                          pointerEvents: 'none',
                          zIndex: 1000,
                          boxSizing: 'border-box'
                        }}
                      >
                        {/* Print margins overlay */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '0.5in',
                            left: '0.5in',
                            right: '0.5in',
                            bottom: '0.5in',
                            border: '1px dashed #f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.03)',
                            boxSizing: 'border-box'
                          }}
                        >
                          {/* Corner markers for printable area */}
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: '2px',
                            width: '8px',
                            height: '8px',
                            borderTop: '2px solid #f59e0b',
                            borderLeft: '2px solid #f59e0b'
                          }}></div>
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            width: '8px',
                            height: '8px',
                            borderTop: '2px solid #f59e0b',
                            borderRight: '2px solid #f59e0b'
                          }}></div>
                          <div style={{
                            position: 'absolute',
                            bottom: '2px',
                            left: '2px',
                            width: '8px',
                            height: '8px',
                            borderBottom: '2px solid #f59e0b',
                            borderLeft: '2px solid #f59e0b'
                          }}></div>
                          <div style={{
                            position: 'absolute',
                            bottom: '2px',
                            right: '2px',
                            width: '8px',
                            height: '8px',
                            borderBottom: '2px solid #f59e0b',
                            borderRight: '2px solid #f59e0b'
                          }}></div>
                        </div>
                        
                        {/* Labels */}
                        <div style={{
                          position: 'absolute',
                          top: '-25px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}>
                          8.5" × 11" Page Boundary
                        </div>
                      </div>
                    )}
                    {/* Selection rectangle with drag handles */}
                    {false && showSelectionTool && (
                      <>
                        {selectionRect && (
                          <div
                            style={{
                              position: 'absolute',
                              left: Math.min(selectionRect.startX, selectionRect.endX),
                              top: Math.min(selectionRect.startY, selectionRect.endY),
                              width: Math.abs(selectionRect.endX - selectionRect.startX),
                              height: Math.abs(selectionRect.endY - selectionRect.startY),
                              border: '2px dashed #3B82F6',
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              zIndex: 1001,
                              pointerEvents: 'none'
                            }}
                          >
                            {/* Corner handles */}
                            <div
                              data-handle="top-left"
                              style={{
                                position: 'absolute',
                                top: -6,
                                left: -6,
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'nw-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            <div
                              data-handle="top-right"
                              style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'ne-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            <div
                              data-handle="bottom-left"
                              style={{
                                position: 'absolute',
                                bottom: -6,
                                left: -6,
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'sw-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            <div
                              data-handle="bottom-right"
                              style={{
                                position: 'absolute',
                                bottom: -6,
                                right: -6,
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'se-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            
                            {/* Side handles */}
                            <div
                              data-handle="top"
                              style={{
                                position: 'absolute',
                                top: -6,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'n-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            <div
                              data-handle="bottom"
                              style={{
                                position: 'absolute',
                                bottom: -6,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 's-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            <div
                              data-handle="left"
                              style={{
                                position: 'absolute',
                                left: -6,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'w-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            <div
                              data-handle="right"
                              style={{
                                position: 'absolute',
                                right: -6,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 12,
                                height: 12,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'e-resize',
                                pointerEvents: 'auto',
                                zIndex: 1002
                              }}
                            />
                            
                            {/* Move handle (center) */}
                            <div
                              data-handle="move"
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 16,
                                height: 16,
                                backgroundColor: '#3B82F6',
                                border: '2px solid white',
                                borderRadius: '4px',
                                cursor: 'move',
                                pointerEvents: 'auto',
                                zIndex: 1002,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                color: 'white'
                              }}
                            >
                              ⊹
                            </div>
                          </div>
                        )}
                        
                        {/* Control buttons */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1002,
                            display: 'flex',
                            gap: '8px'
                          }}
                        >
                          <button
                            onClick={cancelSelection}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                    <h1 className="text-2xl font-bold text-center mb-4">{template.name}</h1>
                    <div style={{ position: 'relative' }}>
                      <table className="rounded-lg w-full border-collapse table-fixed">
                      
                      {/* White overlay for drag columns */}
                      {hideDragColumns && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 'calc(1.5rem + 1.5rem)', // w-6 + w-6 = 1.5rem + 1.5rem = 3rem total
                            height: '100%',
                            backgroundColor: 'white',
                            zIndex: 999,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      
                      {/* White overlay for action column */}
                      {hideActionColumn && showEndColumns && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '5rem', // Increased from 4rem to 5rem to cover more to the left
                            height: '100%',
                            backgroundColor: 'white',
                            zIndex: 999,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                    <thead>
                      <tr className="p-0 bg-white rounded-lg">
                        <th 
                          className={`p-0 text-center font-semibold w-6 transition-all duration-200 ${isDragColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                          onMouseEnter={() => setIsDragColumnHovered(true)}
                          onMouseLeave={() => setIsDragColumnHovered(false)}
                        ></th>
                        <th className={`p-0 text-center font-semibold w-6 border-none text-[7px]`}>#</th>
                        <th className="p-0 relative text-center text-[7px] font-bold text-gray-700 border-none" style={{ width: `${getItemWidth()}px` }}>
                          <div className="p-0">Item</div>
                        </th>
                        <th className="relative text-center text-[7px] font-bold text-gray-700 border-none" style={{ width: `${getObjectiveWidth()}px` }}>
                          <div className="p-0">Objective</div>
                        </th>
                        {showEndColumns && (
                          <>
                            <th className="text-center text-[7px] font-bold text-gray-700 border-none w-8">
                              <div className="p-0">Crit</div>
                            </th>
                            <th className="score-column text-center text-[7px] font-bold text-gray-700 border-none" style={{ width: `${getScoreWidth()}px` }}>
                              <div className="p-0">Score</div>
                            </th>
                            <th className="points-column text-center text-[7px] font-bold text-gray-700 border-none" style={{ width: `${getPointsWidth()}px` }}>
                              <div className="p-0">Pts</div>
                            </th>
                            <th 
                              className={`text-center align-middle text-[7px] font-bold text-gray-700 sticky right-0 bg-white shadow-lg z-10 ${getActionWidthClass()} transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-none' : 'opacity-0 border-transparent'}`}
                              onMouseEnter={() => setIsActionsColumnHovered(true)}
                              onMouseLeave={() => setIsActionsColumnHovered(false)}
                            >
                              <div className="flex items-center justify-center gap-1 relative group">
                                Actions
                                <div className="relative">
                                  <svg 
                                    onClick={() => setShowWidthSettings(!showWidthSettings)}
                                    className="h-3 w-3 text-gray-300 group-hover:text-gray-600 hover:text-gray-800 cursor-pointer transition-colors duration-200 opacity-0 group-hover:opacity-100" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </div>
                              </div>
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                  
                  <Droppable droppableId="assessment-rows">
                    {(provided) => (
                      <tbody {...provided.droppableProps} ref={provided.innerRef}>
                        {template.rows.map((row, index) => {
                          return (
                            <React.Fragment key={row.id}>
                              {/* Simple drop zone before each row */}
                              {isDraggingNewRow && (
                                <tr 
                                  className="h-1 transition-all duration-200 hover:h-8 group"
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragOverIndex(index);
                                  }}
                                  onDragLeave={(e) => {
                                    if (!e.currentTarget.contains(e.relatedTarget)) {
                                      setDragOverIndex(null);
                                    }
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                    if (data.type === 'add-row') {
                                      if (data.rowType === 'description') {
                                        addDescription(index);
                                      } else if (data.rowType === 'custom') {
                                        setShowCustomModuleBuilder(true);
                                      } else {
                                        addRow(data.rowType, index);
                                      }
                                    }
                                    setDragOverIndex(null);
                                    setIsDraggingNewRow(false);
                                  }}
                                >
                                  <td colSpan={showEndColumns ? 8 : 4} className="relative">
                                    <div className={`transition-all duration-200 ${
                                      dragOverIndex === index 
                                        ? 'bg-blue-100 border-2 border-blue-400 border-dashed p-2 text-center text-gray-500 font-medium' 
                                        : 'bg-transparent border-2 border-transparent group-hover:border-blue-300 group-hover:border-dashed group-hover:bg-blue-50'
                                    }`}>
                                      {dragOverIndex === index && `Drop here to insert above row ${index + 1}`}
                                    </div>
                                  </td>
                                </tr>
                              )}

                              <Draggable draggableId={String(row.id)} index={index}>
                                {(provided, snapshot) => {
// ==========================================
// HEADER ROW (type: 'header')
// ==========================================
                                if (row.type === 'header') {
                                  return (
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className="group relative bg-gradient-to-r from-white via-indigo-100 to-white border-l-0 border-r-0 border-indigo-300 hover:from-white hover:via-indigo-150 hover:to-white transition-all duration-200"
                                      data-row-index={index}
                                      data-row-type="header"
                                    >
                                      <td 
                                        {...provided.dragHandleProps} 
                                        className={`header-row p-0 cursor-grab text-center text-indigo-600 font-bold text-lg relative w-6 min-w-6 max-w-6 bg-transparent transition-all duration-200 ${isDragColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                        onMouseEnter={() => setIsDragColumnHovered(true)}
                                        onMouseLeave={() => setIsDragColumnHovered(false)}
                                      >
                                        ⠿
                                      </td>
                                      <td className={`header-row p-0 border-l-0 border-r-0 text-center font-bold text-indigo-800 text-[7px] w-6 min-w-6 max-w-6 bg-transparent`} >
                                      </td>
                                      <td colSpan={showEndColumns ? 5 : 1} className="header-row p-0 border-l-0 border-r-0 bg-gradient-to-r from-white via-indigo-100 to-white relative text-center">
                                        <div className="p-0 border-l-0 border-r-0 font-poppins font-bold capitalize text-[11px]">
                                          <EditableCell
                                            value={row.text}
                                            onChange={(value) => handleRowChange(index, 'text', value)}
                                            onConfirm={handleCellConfirm}
                                            onCancel={handleCellCancel}
                                            isHeader={true}
                                            rowIndex={index}
                                            field="text"
                                            editingCell={editingCell}
                                            setEditingCell={setEditingCell}
                                            tempCellValue={tempCellValue}
                                            setTempCellValue={setTempCellValue}
                                            placeholder="Header Text"
                                          />
                                        </div>
                                      </td>
                                      {showEndColumns && (
                                        <td 
                                          className={`header-row text-center align-middle bg-transparent p-0 sticky right-0 w-16 min-w-16 max-w-16 transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-l-0' : 'opacity-0 border-transparent'}`}
                                          onMouseEnter={() => setIsActionsColumnHovered(true)}
                                          onMouseLeave={() => setIsActionsColumnHovered(false)}
                                        >
                                          <button 
                                            type="button" 
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded-full transition-all duration-200"
                                            onClick={() => removeRow(index)} 
                                            title="Delete Header"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                }

// ==========================================
// SUB-HEADER ROW (type: 'sub-header')
// ==========================================
                                if (row.type === 'sub-header') {
                                  return (
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className="p-0 border-l-0 border-r-0 group relative hover:bg-gradient-to-r hover:from-white hover:via-purple-75 hover:to-white transition-all duration-200"
                                      data-row-index={index}
                                      data-row-type="sub-header"
                                    >
                                      <td 
                                        {...provided.dragHandleProps} 
                                        className={`subheader-row cursor-grab text-center text-purple-600 p-0 font-bold relative w-6 min-w-6 max-w-6 bg-transparent transition-all duration-200 ${isDragColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                        onMouseEnter={() => setIsDragColumnHovered(true)}
                                        onMouseLeave={() => setIsDragColumnHovered(false)}
                                      >
                                        ⠿
                                      </td>
                                      <td className="subheader-row text-center font-bold text-purple-700 text-xs p-0 w-6 min-w-6 max-w-6 bg-transparent border-none">
                                      </td>
                                      <td colSpan={5} className="subheader-row bg-gradient-to-r from-white from-25% via-purple-100 via-50% to-white to-75% relative p-0 border-l-0 border-r-0 text-center uppercase shadow-inner">
                                        <div className="flex items-center justify-center border-l-0 border-r-0">
                                          <div className="flex-auto font-poppins font-bold capitalize text-[10px] border-l-0 border-r-0">
                                            <EditableCell
                                              value={row.text}
                                              onChange={(value) => handleRowChange(index, 'text', value)}
                                              onConfirm={handleCellConfirm}
                                              onCancel={handleCellCancel}
                                              isHeader={true}
                                              isSubHeader={true}
                                              rowIndex={index}
                                              field="text"
                                              editingCell={editingCell}
                                              setEditingCell={setEditingCell}
                                              tempCellValue={tempCellValue}
                                              setTempCellValue={setTempCellValue}
                                              placeholder="Section Name"
                                            />
                                          </div>
                                        </div>
                                      </td>
                                      <td 
                                        className={`subheader-row text-center align-middle bg-transparent p-0 sticky right-0 w-16 min-w-16 max-w-16 transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-l-0' : 'opacity-0 border-transparent'}`}
                                        onMouseEnter={() => setIsActionsColumnHovered(true)}
                                        onMouseLeave={() => setIsActionsColumnHovered(false)}
                                      >
                                        <button 
                                          type="button" 
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0 rounded-full transition-all duration-200"
                                          onClick={() => removeRow(index)} 
                                          title="Delete Sub-Header"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                          </svg>
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                }

// ==========================================
// REGULAR TASK ROW AND CUSTOM MODULE ROW NUMBERING
// ==========================================
                                let rowNumber = 0;
                                for (let i = 0; i <= index; i++) {
                                  const currentRow = template.rows[i];
                                  if (currentRow.type === 'regular' || currentRow.type === 'custom') {
                                    rowNumber += 1;
                                  }
                                }

// ==========================================
// CUSTOM MODULE ROW (type: 'custom')
// ==========================================
                                if (row.type === 'custom') {

 // Mobile layout - completely different structure
                                  if (isMobile) {
                                    return (
                                      <tr 
                                        {...provided.draggableProps} 
                                        ref={provided.innerRef} 
                                        className="group relative bg-white"
                                        data-row-index={index}
                                        data-row-type="custom"
                                      >
                                        <td colSpan={showEndColumns ? 8 : 4} className="border-b border-gray-300">
                                          <div className= "border-blue-500">
{/* Mobile Header */}
                                            <div className="flex items-center justify-between p-3 bg-blue-50 border-b border-blue-200">
                                              <div className="flex items-center gap-3">
                                                <div 
                                                  {...provided.dragHandleProps} 
                                                  className={`cursor-grab text-blue-600 font-bold transition-all duration-200 ${isDragColumnHovered ? 'opacity-100' : 'opacity-0'}`}
                                                  onMouseEnter={() => setIsDragColumnHovered(true)}
                                                  onMouseLeave={() => setIsDragColumnHovered(false)}
                                                >
                                                  ⠿
                                                </div>
                                                <div className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold`} >
                                                  {rowNumber}
                                                </div>
                                                <div className="font-semibold text-blue-800 text-[11px] text-center whitespace-pre-line leading-tight">
                                                  {row.itemText || 'Custom Item'}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                {row.critical && (
                                                  <div className="text-red-600" title="Critical Item">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                      <path d="M10 2L18 16H2L10 2Z"/>
                                                      <circle cx="10" cy="13" r="1"/>
                                                      <path d="M10 7v3" strokeLinecap="round"/>
                                                    </svg>
                                                  </div>
                                                )}
                                                <button 
                                                  type="button" 
                                                  className="text-blue-500 hover:text-blue-700 p-1"
                                                  onClick={() => editCustomModule(row, index)} 
                                                  title="Edit Custom Module"
                                                >
                                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                  </svg>
                                                </button>
                                                <button 
                                                  type="button" 
                                                  className="text-red-500 hover:text-red-700 p-1"
                                                  onClick={() => removeRow(index)} 
                                                  title="Delete Custom Module"
                                                >
                                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                                  </svg>
                                                </button>
                                              </div>
                                            </div>

{/* Mobile Custom Module Grid */}
                                            <div className="p-4">
                                              <div 
                                                className={`grid w-full max-w-full overflow-hidden ${row.columns >= 8 ? 'gap-0' : 'gap-1'}`}
                                                style={{
                                                  gridTemplateColumns: `repeat(${row.columns || 2}, minmax(${getCustomModuleGridSize(row.itemText, row.columns, true)}, 1fr))`,
                                                  gridTemplateRows: `repeat(${row.rows || 2}, minmax(20px, auto))`,
                                                }}
                                              >
                                                {row.cells?.map((cellRow, rowIdx) => 
                                                  cellRow.map((cell, colIdx) => (
                                                    <div key={`${rowIdx}-${colIdx}`} className={`${cell.editable ? 'bg-white border-none' : 'bg-purple-100 border border-purple-300'} rounded-lg p-1 flex items-center justify-center min-h-[20px] shadow-sm max-w-full overflow-hidden`}>
                                                      {cell.type === 'checkbox' ? (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                          <div className={`flex items-center justify-center ${
                                                            cell.checkboxPosition === 'left' 
                                                              ? 'flex-row gap-2 w-full' 
                                                              : 'flex-col gap-2 w-full h-full'
                                                          }`}>
                                                            {cell.checkboxPosition === 'left' && (
                                                              <div className="flex items-center justify-center mb-1">
                                                                <input 
                                                                  type="checkbox" 
                                                                  checked={cell.checked || false}
                                                                  onChange={(e) => {
                                                                    const updatedCells = [...row.cells];
                                                                    updatedCells[rowIdx][colIdx] = { ...cell, checked: e.target.checked };
                                                                    handleRowChange(index, 'cells', updatedCells);
                                                                  }}
                                                                  className={`${getCustomModuleCheckboxSize(row.columns)} text-blue-600 focus:ring-blue-500`}
                                                                />
                                                              </div>
                                                            )}
                                                            <div className="flex-auto flex items-center justify-center min-w-0">
                                                              {cell.editable ? (
                                                                <EditableCell
                                                                  value={cell.text}
                                                                  onChange={(value) => {
                                                                    const updatedCells = [...row.cells];
                                                                    updatedCells[rowIdx][colIdx] = { ...cell, text: value };
                                                                    handleRowChange(index, 'cells', updatedCells);
                                                                  }}
                                                                  onConfirm={handleCellConfirm}
                                                                  onCancel={handleCellCancel}
                                                                  isHeader={false}
                                                                  rowIndex={`${index}-${rowIdx}-${colIdx}`}
                                                                  field="customCell"
                                                                  editingCell={editingCell}
                                                                  setEditingCell={setEditingCell}
                                                                  tempCellValue={tempCellValue}
                                                                  setTempCellValue={setTempCellValue}
                                                                  placeholder="Text..."
                                                                  useItalic={cell.italic}
                                                                  customCellData={cell}
                                                                />
                                                              ) : (
                                                                <div className={`${getCustomModuleTextSize(row.columns)} text-${cell.textAlign || 'center'} leading-tight break-words w-full ${row.columns >= 8 ? 'px-0' : 'px-1'} ${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>
                                                                  {cell.text}
                                                                </div>
                                                              )}
                                                            </div>
                                                            {cell.checkboxPosition === 'bottom' && (
                                                              <div className="flex items-center justify-center mb-1">
                                                                <input 
                                                                  type="checkbox" 
                                                                  checked={cell.checked || false}
                                                                  onChange={(e) => {
                                                                    const updatedCells = [...row.cells];
                                                                    updatedCells[rowIdx][colIdx] = { ...cell, checked: e.target.checked };
                                                                    handleRowChange(index, 'cells', updatedCells);
                                                                  }}
                                                                  className={`${getCustomModuleCheckboxSize(row.columns)} text-blue-600 focus:ring-blue-500`}
                                                                />
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <div className="flex flex-col justify-center items-center w-full h-full min-w-0">
                                                          {cell.editable ? (
                                                            <EditableCell
                                                              value={cell.text}
                                                              onChange={(value) => {
                                                                const updatedCells = [...row.cells];
                                                                updatedCells[rowIdx][colIdx] = { ...cell, text: value };
                                                                handleRowChange(index, 'cells', updatedCells);
                                                              }}
                                                              onConfirm={handleCellConfirm}
                                                              onCancel={handleCellCancel}
                                                              isHeader={false}
                                                              rowIndex={`${index}-${rowIdx}-${colIdx}`}
                                                              field="customCell"
                                                              editingCell={editingCell}
                                                              setEditingCell={setEditingCell}
                                                              tempCellValue={tempCellValue}
                                                              setTempCellValue={setTempCellValue}
                                                              placeholder="Text..."
                                                              useItalic={cell.italic}
                                                              customCellData={cell}
                                                            />
                                                          ) : (
                                                            <div className={`px-1 text-${cell.textAlign || 'center'} text-[11px] leading-tight break-words w-full max-w-full overflow-hidden ${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>
                                                              {cell.text}
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ))
                                                ).flat()}
                                              </div>
                                            </div>

{/* Mobile Footer with Score/Actions */}
                                            {showEndColumns && (
                                              <div className="p-3 bg-gray-100 border-t border-gray-200 flex items-center justify-between text-sm">
                                                <div className="flex items-center gap-4">
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-gray-600">Critical:</span>
                                                    <button
                                                      type="button"
                                                      onClick={() => handleRowChange(index, 'critical', !row.critical)}
                                                      className={`w-5 h-5 flex items-center justify-center transition-all duration-200 ${
                                                        row.critical 
                                                          ? 'text-red-600 hover:text-red-700' 
                                                          : 'text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100'
                                                      }`}
                                                      title={row.critical ? "Remove Critical Status" : "Mark as Critical"}
                                                    >
                                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                        <path d="M10 2L18 16H2L10 2Z" stroke="currentColor" fill="none"/>
                                                        <circle cx="10" cy="13" r="1" fill="currentColor"/>
                                                        <path d="M10 7v3" stroke="currentColor" strokeLinecap="round"/>
                                                      </svg>
                                                    </button>
                                                  </div>
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-gray-600">Score:</span>
                                                    <span className="score-column font-medium">{row.scoreAwarded || ''}</span>
                                                  </div>
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-gray-600">Points:</span>
                                                    <span className="text-sm font-medium">
                                                      {row.pointsPossible || 1}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  }

// DESKTOP LAYOUT - table row
                                  return (
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className="group relative hover:shadow-md transition-all duration-200 bg-white hover:bg-gray-50 border-r-0 p-0"
                                      data-row-index={index}
                                      data-row-type="custom"
                                    >
                                      <td 
                                        {...provided.dragHandleProps} 
                                        className={`cursor-grab text-center text-gray-600 font-bold p-0 relative w-6 min-w-6 max-w-6 transition-all duration-200 ${isDragColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                        onMouseEnter={() => setIsDragColumnHovered(true)}
                                        onMouseLeave={() => setIsDragColumnHovered(false)}
                                      >
                                        ⠿
                                      </td>
                                      <td className="text-center font-semibold text-black text-xs bg-white w-auto">
                                        {rowNumber}     
                                      </td>

{/* Comment items Normal Row */}
                                      <td className={`bg-white font-medium ${getItemWidthClass()}`}>
                                        <div className="text-[11px] font-semibold text-blue-800 flex items-center justify-center text-center whitespace-pre-line leading-tight">
                                          {row.itemText || 'Custom Item'}
                                        </div>
                                      </td>

{/* Comment BG Container for Custom Cells */}
                                      <td className={`bg-white align-center ${getObjectiveWidthClass()}`}>
                                        <div className="p-1 h-full w-full max-w-full overflow-hidden">
                                          <div 
                                            className={`grid w-full max-w-full ${row.columns >= 8 ? 'gap-0' : 'gap-1'}`}
                                            style={{
                                              gridTemplateColumns: `repeat(${row.columns || 2}, minmax(${getCustomModuleGridSize(row.itemText, row.columns, false)}, 1fr))`,
                                              gridTemplateRows: `repeat(${row.rows || 2}, minmax(1.5rem, auto))`,
                                            }}
                                          >

{/* Comment Text Container for Custom Cells */}
                                            {row.cells?.map((cellRow, rowIdx) => 
                                              cellRow.map((cell, colIdx) => (                                                                              
                                                <div key={`${rowIdx}-${colIdx}`} className={`${cell.editable ? 'bg-amber-50 border border-gray-400' : 
 // Comment Custom Item Text/Checkbox Container //                                                
                                                'bg-slate-50'} min-h-[1.9rem] w-full max-w-full overflow-hidden`}>
                                                  {cell.type === 'checkbox' ? (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                      <div className={`${getCustomModulePadding(row.columns)} flex items-center justify-center gap-1 ${
                                                        cell.checkboxPosition === 'left' 
                                                          ? 'flex-row w-full' 
                                                          : cell.checkboxPosition === 'right'
                                                          ? 'flex-row-reverse w-full'
                                                          : 'flex-col w-full py-1'
                                                      }`}>
                                                        {(cell.checkboxPosition === 'left' || cell.checkboxPosition === 'right') && (
                                                          <div className="flex">
                                                            <input 
                                                              type="checkbox" 
                                                              checked={cell.checked || false}
                                                              onChange={(e) => {
                                                                const updatedCells = [...row.cells];
                                                                updatedCells[rowIdx][colIdx] = { ...cell, checked: e.target.checked };
                                                                handleRowChange(index, 'cells', updatedCells);
                                                              }}
                                                              className={`${getCustomModuleCheckboxSize(row.columns)} text-blue-600 focus:ring-blue-500`}
                                                            />
                                                          </div>
                                                        )}
                                                        <div className="flex-auto">
                                                          {cell.editable ? (
                                                            <EditableCell
                                                              value={cell.text}
                                                              onChange={(value) => {
                                                                const updatedCells = [...row.cells];
                                                                updatedCells[rowIdx][colIdx] = { ...cell, text: value };
                                                                handleRowChange(index, 'cells', updatedCells);
                                                              }}
                                                              onConfirm={handleCellConfirm}
                                                              onCancel={handleCellCancel}
                                                              isHeader={false}
                                                              rowIndex={`${index}-${rowIdx}-${colIdx}`}
                                                              field="customCell"
                                                              editingCell={editingCell}
                                                              setEditingCell={setEditingCell}
                                                              tempCellValue={tempCellValue}
                                                              setTempCellValue={setTempCellValue}
                                                              placeholder="Text..."
                                                              useItalic={cell.italic}
                                                              customCellData={cell}
                                                            />
                                                          ) : (
// Custom Item text with styles //
                                                            <div className={`${getCustomModuleTextSize(row.columns)} text-${cell.textAlign || 'center'} break-words w-full max-w-full overflow-hidden ${row.columns >= 8 ? 'px-0' : 'px-1'} ${cell.bold ? 'font-semibold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>{cell.text}</div>
                                                          )}
                                                        </div>
                                                        {cell.checkboxPosition === 'bottom' && (
                                                          <div className="flex">
                                                            <input 
                                                              type="checkbox" 
                                                              checked={cell.checked || false}
                                                              onChange={(e) => {
                                                                const updatedCells = [...row.cells];
                                                                updatedCells[rowIdx][colIdx] = { ...cell, checked: e.target.checked };
                                                                handleRowChange(index, 'cells', updatedCells);
                                                              }}
                                                              className={`${getCustomModuleCheckboxSize(row.columns)} text-blue-600 focus:ring-blue-500`}
                                                            />
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <div className={`w-full max-w-full overflow-hidden min-h-[1.5rem] ${getCustomModulePadding(row.columns)}`}>
                                                      {cell.editable ? (
                                                        <EditableCell
                                                          value={cell.text}
                                                          onChange={(value) => {
                                                            const updatedCells = [...row.cells];
                                                            updatedCells[rowIdx][colIdx] = { ...cell, text: value };
                                                            handleRowChange(index, 'cells', updatedCells);
                                                          }}
                                                          onConfirm={handleCellConfirm}
                                                          onCancel={handleCellCancel}
                                                          isHeader={false}
                                                          rowIndex={`${index}-${rowIdx}-${colIdx}`}
                                                          field="customCell"
                                                          editingCell={editingCell}
                                                          setEditingCell={setEditingCell}
                                                          tempCellValue={tempCellValue}
                                                          setTempCellValue={setTempCellValue}
                                                          placeholder="Text..."
                                                          useItalic={cell.italic}
                                                          customCellData={cell}
                                                        />
                                                      ) : (

 // Custom Item text 2 (OPQRST/SAMPLE) //                                                       
                                                        <div className={`text-${cell.textAlign || 'center'} ${getCustomModuleTextSize(row.columns)} break-words w-full max-w-full overflow-hidden ${row.columns >= 8 ? 'px-0' : 'px-1'} ${cell.bold ? 'font-semibold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>{cell.text}</div>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              ))
                                            ).flat()}
                                          </div>
                                        </div>
                                      </td>
                                      {showEndColumns && (
                                        <>
                                          <td className={`text-center bg-white ${getCriticalWidthClass()}`} >
                                            <div className="flex items-center justify-center h-auto">
                                              <button
                                                type="button"
                                                onClick={() => handleRowChange(index, 'critical', !row.critical)}
                                                className={`w-auto h-auto flex items-center justify-center transition-all duration-200 ${
                                                  row.critical 
                                                    ? 'text-red-600 hover:text-red-700' 
                                                    : 'text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100'
                                                }`}
                                                title={row.critical ? "Remove Critical Status" : "Mark as Critical"}
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                  <path d="M10 2L18 16H2L10 2Z" stroke="currentColor" fill="none"/>
                                                  <circle cx="10" cy="13" r="1" fill="currentColor"/>
                                                  <path d="M10 7v3" stroke="currentColor" strokeLinecap="round"/>
                                                </svg>
                                              </button>
                                            </div>
                                          </td>
                                          <td className="score-column text-center border-r border-gray-300 bg-white" style={{ width: `${getScoreWidth()}px` }}>
                                            <div className="text-sm font-medium text-gray-700">
{/* Score */}
                                              {row.scoreAwarded || ''}
                                            </div>
                                          </td>
                                          <td className="points-column text-center p-0 border border-gray-300 bg-white" style={{ width: `${getPointsWidth()}px` }}>
                                            <span className="text-sm font-medium">
                                              {row.pointsPossible || 1}
                                            </span>
                                          </td>
                                          <td 
                                            className={`text-center bg-white p-0 sticky right-0 w-16 min-w-16 max-w-16 transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                            onMouseEnter={() => setIsActionsColumnHovered(true)}
                                            onMouseLeave={() => setIsActionsColumnHovered(false)}
                                          >
                                            <div className="flex flex-col items-center justify-center h-auto w-auto p-0">
                                              <button 
                                                type="button" 
                                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-1 rounded-full transition-all duration-200 flex items-center justify-center"
                                                onClick={() => editCustomModule(row, index)} 
                                                title="Edit Custom Module"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                </svg>
                                              </button>
                                              <button 
                                                type="button" 
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded-full transition-all duration-200 flex items-center justify-center"
                                                onClick={() => removeRow(index)} 
                                                title="Delete Custom Module"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            </div>
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  );
                                }

// ==========================================
// DESCRIPTION ROW (type: 'description')
// ==========================================
                                if (row.type === 'description') {
                                  return (
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className="group relative border border-gray-300 transition-colors duration-200"
                                      data-row-index={index}
                                      data-row-type="description"
                                    >
                                      <td 
                                        {...provided.dragHandleProps} 
                                        className={`description-row cursor-grab text-center text-amber-600 bg--50 py-1 px-1 font-bold relative w-6 min-w-6 max-w-6 transition-all duration-200 ${isDragColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                        onMouseEnter={() => setIsDragColumnHovered(true)}
                                        onMouseLeave={() => setIsDragColumnHovered(false)}
                                      >
                                        ⠿
                                      </td>
                                      <td className="description-row text-center font-bold text-amber-700 text-xs bg-amber-100 py-1 px-1 w-6 min-w-6 max-w-6">
                                        DESC
                                      </td>
                                      <td colSpan={showEndColumns ? 5 : 1} className="description-row bg-amber-50 relative py-0.5 px-1 text-center italic border-r border-gray-300">
                                        <div className="font-poppins font-normal italic">
                                          <EditableCell
                                            value={row.text}
                                            onChange={(value) => handleRowChange(index, 'text', value)}
                                            onConfirm={handleCellConfirm}
                                            onCancel={handleCellCancel}
                                            isDescription={true}
                                            rowIndex={index}
                                            field="text"
                                            editingCell={editingCell}
                                            setEditingCell={setEditingCell}
                                            tempCellValue={tempCellValue}
                                            setTempCellValue={setTempCellValue}
                                            placeholder="Description text..."
                                          />
                                        </div>
                                      </td>
                                      {showEndColumns && (
                                        <td 
                                          className={`description-row text-center align-middle py-0.5 px-1 sticky right-0 w-16 min-w-16 max-w-16 transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                          onMouseEnter={() => setIsActionsColumnHovered(true)}
                                          onMouseLeave={() => setIsActionsColumnHovered(false)}
                                        >
                                          <button 
                                            type="button" 
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded-full transition-all duration-200"
                                            onClick={() => removeRow(index)} 
                                            title="Delete Description"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                }

// ==========================================
// REGULAR TASK ROW (default)
// ==========================================
                                return (
// Mobile layout for regular rows
                                  isMobile ? (
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className={`group relative border-none bg-white ${snapshot.isDragging ? 'bg-white border-blue-400 p-0' : ''}`}
                                      data-row-index={index}
                                    >
                                      <td colSpan={showEndColumns ? 8 : 4} className="p-0">
                                        <div className="border-l-4 border-blue-500 bg-white">
                                          {/* Mobile Header */}
                                          <div className="flex items-center justify-between p-3 bg-blue-50 border-b border-blue-200">
                                            <div className="flex items-center gap-3">
                                              <div 
                                                {...provided.dragHandleProps} 
                                                className={`cursor-grab text-blue-600 font-bold transition-all duration-200 ${isDragColumnHovered ? 'opacity-100' : 'opacity-0'}`}
                                                onMouseEnter={() => setIsDragColumnHovered(true)}
                                                onMouseLeave={() => setIsDragColumnHovered(false)}
                                              >
                                                ⠿
                                              </div>
                                              <div className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold`} >
                                                {rowNumber}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              {row.critical && (
                                                <div className="text-red-600" title="Critical Item">
                                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M10 2L18 16H2L10 2Z"/>
                                                    <circle cx="10" cy="13" r="1"/>
                                                    <path d="M10 7v3" strokeLinecap="round"/>
                                                  </svg>
                                                </div>
                                              )}
                                              <button
                                                onClick={() => removeRow(index)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                                title="Delete Row"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            </div>
                                          </div>

{/* Mobile Content */}
                                          <div className="p-3 space-y-3">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-600 mb-1">Task:</label>
                                              <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                                                <EditableCell
                                                  value={row.task}
                                                  onChange={(value) => handleRowChange(index, 'task', value)}
                                                  onConfirm={handleCellConfirm}
                                                  onCancel={handleCellCancel}
                                                  isHeader={false}
                                                  rowIndex={index}
                                                  field="task"
                                                  editingCell={editingCell}
                                                  setEditingCell={setEditingCell}
                                                  tempCellValue={tempCellValue}
                                                  setTempCellValue={setTempCellValue}
                                                  isTextarea={true}
                                                  placeholder="Enter task description..."
                                                />
                                              </div>
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-600">Criteria:</label>
                                              <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                                                <EditableCell
                                                  value={row.criteria}
                                                  onChange={(value) => handleRowChange(index, 'criteria', value)}
                                                  onConfirm={handleCellConfirm}
                                                  onCancel={handleCellCancel}
                                                  isHeader={false}
                                                  rowIndex={index}
                                                  field="criteria"
                                                  editingCell={editingCell}
                                                  setEditingCell={setEditingCell}
                                                  tempCellValue={tempCellValue}
                                                  setTempCellValue={setTempCellValue}
                                                  isTextarea={true}
                                                  placeholder="Enter success criteria..."
                                                  useItalic={true}
                                                />
                                              </div>
                                            </div>
                                          </div>

{/* Mobile Footer with Score/Actions */}
                                          {showEndColumns && (
                                            <div className="p-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-sm">
                                              <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-1">
                                                  <span className="text-gray-600">Critical:</span>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRowChange(index, 'critical', !row.critical)}
                                                    className={`w-5 h-5 flex items-center justify-center transition-all duration-200 ${
                                                      row.critical 
                                                        ? 'text-red-600 hover:text-red-700' 
                                                        : 'text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100'
                                                    }`}
                                                    title={row.critical ? "Remove Critical Status" : "Mark as Critical"}
                                                  >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                      <path d="M10 2L18 16H2L10 2Z" stroke="currentColor" fill="none"/>
                                                      <circle cx="10" cy="13" r="1" fill="currentColor"/>
                                                      <path d="M10 7v3" stroke="currentColor" strokeLinecap="round"/>
                                                    </svg>
                                                  </button>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <span className="text-gray-600">Score:</span>
                                                  <input
                                                    type="text"
                                                    value={row.scoreAwarded || ''}
                                                    onChange={(e) => handleRowChange(index, 'scoreAwarded', e.target.value)}
                                                    className="score-column w-12 text-center border border-gray-300 rounded px-1 py-0.5 text-sm"
                                                    placeholder=""
                                                  />
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <span className="text-gray-600">Points:</span>
                                                  <span className="text-sm font-medium">
                                                    {row.pointsPossible || 1}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ) : (

// Desktop layout
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className={`group relative border-none hover:shadow-md transition-all duration-200 bg-white hover:bg-gray-50 ${snapshot.isDragging ? 'bg-white border-blue-400' : ''}`}
                                      data-row-index={index}
                                    >
                                      <td 
                                        {...provided.dragHandleProps} 
                                        className={`cursor-grab text-center text-gray-500 hover:text-gray-700 h-auto relative w-6 min-w-6 max-w-6 transition-all duration-200 ${isDragColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                        onMouseEnter={() => setIsDragColumnHovered(true)}
                                        onMouseLeave={() => setIsDragColumnHovered(false)}
                                      >
                                        ⠿
                                      </td>
                                      <td className={`text-center font-semibold text-gray-700 h-7 w-6 min-w-6 max-w-6 text-xs`} >
                                        {rowNumber}
                                      </td>
                                      
{/* Task Cell */}
                                      <td className={`font-semibold text-[11px] text-center cursor-pointer transition-all duration-200 h-auto px-1 text-blue-800 ${getItemWidthClass()}`}>
                                        <EditableCell
                                          value={row.task}
                                          onChange={(value) => handleRowChange(index, 'task', value)}
                                          onConfirm={handleCellConfirm}
                                          onCancel={handleCellCancel}
                                          isHeader={false}
                                          rowIndex={index}
                                          field="task"
                                          editingCell={editingCell}
                                          setEditingCell={setEditingCell}
                                          tempCellValue={tempCellValue}
                                          setTempCellValue={setTempCellValue}
                                          isTextarea={true}
                                          placeholder="Enter task description..."
                                        />
                                      </td>
                                      
{/* Criteria Cell */}
                                      <td className={`bg-amber-50 border border-gray-400 text-[11px] cursor-pointer transition-all duration-200 h-auto px-1 ${getObjectiveWidthClass()}`}>
                                        <EditableCell
                                          value={row.criteria}
                                          onChange={(value) => handleRowChange(index, 'criteria', value)}
                                          onConfirm={handleCellConfirm}
                                          onCancel={handleCellCancel}
                                          isHeader={false}
                                          rowIndex={index}
                                          field="criteria"
                                          editingCell={editingCell}
                                          setEditingCell={setEditingCell}
                                          tempCellValue={tempCellValue}
                                          setTempCellValue={setTempCellValue}
                                          isTextarea={true}
                                          placeholder="Enter text here..."
                                          useItalic={true}
                                        />
                                      </td>

{/* Critical Cell */}
                                      {showEndColumns && (
                                        <td className={`border-r border-gray-300 text-center align-middle h-auto px-1 ${getCriticalWidthClass()}`} >
                                          <div className="flex items-center justify-center h-full">
                                            <button
                                              type="button"
                                              onClick={() => handleRowChange(index, 'critical', !row.critical)}
                                              className={`w-5 h-5 flex items-center justify-center transition-all duration-200 ${
                                                row.critical 
                                                  ? 'text-red-600 hover:text-red-700' 
                                                  : 'text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100'
                                              }`}
                                              title={row.critical ? "Remove Critical Status" : "Mark as Critical"}
                                            >
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M10 2L18 16H2L10 2Z" stroke="currentColor" fill="none"/>
                                                <circle cx="10" cy="13" r="1" fill="currentColor"/>
                                                <path d="M10 7v3" stroke="currentColor" strokeLinecap="round"/>
                                              </svg>
                                            </button>
                                          </div>
                                        </td>
                                      )}

{/* Score Cell */}
                                      {showEndColumns && (
                                        <td className="score-column border-r border-gray-300 text-center align-middle h-auto px-1" style={{ width: `${getScoreWidth()}px` }}>
                                          <input
                                            type="text"
                                            value={row.scoreAwarded || ''}
                                            onChange={(e) => handleRowChange(index, 'scoreAwarded', e.target.value)}
                                            className="w-full text-center border-none focus:outline-none focus:ring-0 text-sm bg-transparent h-auto"
                                            placeholder=""
                                          />
                                        </td>
                                      )}

{/* Points Cell */}
                                      {showEndColumns && (
                                        <td className="points-column border-r border-gray-300 text-center align-middle h-auto px-1" style={{ width: `${getPointsWidth()}px` }}>
                                          <span className="text-sm">
                                            {row.pointsPossible || 1}
                                          </span>
                                        </td>
                                      )}
                                      
{/* Actions Cell */}
                                      {showEndColumns && (
                                        <td 
                                          className={`text-center py-0 sticky right-0 bg-white w-16 min-w-16 max-w-16 transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-r border-gray-300' : 'opacity-0 border-transparent'}`}
                                          onMouseEnter={() => setIsActionsColumnHovered(true)}
                                          onMouseLeave={() => setIsActionsColumnHovered(false)}
                                        >
                                          <div className="flex items-center justify-center">
                                            <button
                                              onClick={() => removeRow(index)}
                                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-all duration-200"
                                              title="Delete Row"
                                            >
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                              </svg>
                                            </button>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  )
                                );
                              }}
                              </Draggable>
                            </React.Fragment>
                          );
                        })}
                        
                        {/* Final drop zone at the end of all rows */}
                        {isDraggingNewRow && (
                          <tr 
                            className="h-1 transition-all duration-200 hover:h-8 group"
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverIndex(template.rows.length);
                            }}
                            onDragLeave={(e) => {
                              if (!e.currentTarget.contains(e.relatedTarget)) {
                                setDragOverIndex(null);
                              }
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                              if (data.type === 'add-row') {
                                if (data.rowType === 'description') {
                                  addDescription();
                                } else if (data.rowType === 'custom') {
                                  setShowCustomModuleBuilder(true);
                                } else {
                                  addRow(data.rowType);
                                }
                              }
                              setDragOverIndex(null);
                              setIsDraggingNewRow(false);
                            }}
                          >
                            <td colSpan={showEndColumns ? 8 : 4} className="relative">
                              <div className={`transition-all duration-200 ${
                                dragOverIndex === template.rows.length 
                                  ? 'bg-blue-100 border-2 border-blue-400 border-dashed p-2 text-center text-gray-500 font-medium' 
                                  : 'bg-transparent border-2 border-transparent group-hover:border-blue-300 group-hover:border-dashed group-hover:bg-blue-50'
                              }`}>
                                {dragOverIndex === template.rows.length && `Drop here to add at end`}
                              </div>
                            </td>
                          </tr>
                        )}
                        
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                  
                  {/* Total Points Row - Always at bottom */}
                  <tbody>
                    <tr className={`p-0 bg-white rounded-lg transition-all duration-200 ${isActionsColumnHovered ? 'border-t-2 border-gray-300' : 'border-t-2 border-transparent'}`}>
                      <th className={`p-0 text-center font-bold text-gray-700 w-6 min-w-6 max-w-6 border-none text-[7px]`} >
                        ∑
                      </th>
                      <th className={`p-0 text-center font-bold text-gray-700 w-6 min-w-6 max-w-6 border-none`} >
                      </th>
                      <th className={`p-0 text-center text-[7px] font-bold text-gray-700 border-none ${getItemWidthClass()}`}>
                        <div className="p-0">TOTAL POINTS</div>
                      </th>
                      <th className={`p-0 text-center text-[7px] font-bold text-gray-700 border-none ${getObjectiveWidthClass()}`}>
                        <div className="p-0">Assessment Total</div>
                      </th>
                      {showEndColumns && (
                        <>
                          <th className={`text-center text-[7px] font-bold text-gray-700 border-none ${getCriticalWidthClass()}`} >
                            <div className="p-0"></div>
                          </th>
                          <th className={`score-column text-center text-[7px] font-bold text-gray-700 border-none ${getScoreWidthClass()}`}>
                            <div className="p-0 assessment-total-score" style={{ height: '16px' }}></div>
                          </th>
                          <th className={`points-column text-center text-[7px] font-bold text-gray-700 border-none ${getPointsWidthClass()}`}>
                            <div className="p-0 text-sm">{calculateTotalPoints()}</div>
                          </th>
                          <th 
                            className={`text-center align-middle text-[7px] font-bold text-gray-700 sticky right-0 w-16 min-w-16 max-w-16 transition-all duration-200 ${isActionsColumnHovered ? 'opacity-100 border-none' : 'opacity-0 border-transparent'}`}
                            onMouseEnter={() => setIsActionsColumnHovered(true)}
                            onMouseLeave={() => setIsActionsColumnHovered(false)}
                          >
                            <div className="p-0"></div>
                          </th>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
                    </div> {/* End table container with overlay */}
                  </div> {/* End printable-assessment */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>

{/* Custom Module Builder Modal */}
      <CustomModuleBuilder
        isOpen={showCustomModuleBuilder}
        onClose={() => {
          setShowCustomModuleBuilder(false);
          setEditingCustomModule(null);
        }}
        onSave={editingCustomModule ? updateCustomModule : handleCustomModuleSave}
        editingModule={editingCustomModule}
      />

      {/* Column Width Settings Popup */}
      {showWidthSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-xs w-full mx-4 pointer-events-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Column Widths</h3>
              <button
                onClick={() => setShowWidthSettings(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Item Width Slider */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Item</label>
                <span className="text-xs text-gray-500">
                  {columnWidthAdjustment > 0 ? '+' : ''}{columnWidthAdjustment}
                </span>
              </div>
              <input
                type="range"
                min="-100"
                max="100"
                value={columnWidthAdjustment}
                onChange={(e) => setColumnWidthAdjustment(parseInt(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* Score Width Slider */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Score</label>
                <span className="text-xs text-gray-500">{24 + scoreColumnWidth}px</span>
              </div>
              <input
                type="range"
                min="-8"
                max="40"
                value={scoreColumnWidth}
                onChange={(e) => setScoreColumnWidth(parseInt(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* Points Width Slider */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Points</label>
                <span className="text-xs text-gray-500">{32 + pointsColumnWidth}px</span>
              </div>
              <input
                type="range"
                min="-12"
                max="40"
                value={pointsColumnWidth}
                onChange={(e) => setPointsColumnWidth(parseInt(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Container Width Slider */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Container</label>
                <span className="text-xs text-gray-500">{containerWidth}vw</span>
              </div>
              <input
                type="range"
                min="50"
                max="100"
                value={containerWidth}
                onChange={(e) => setContainerWidth(parseInt(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Reset Button */}
            <button
              onClick={() => {
                setColumnWidthAdjustment(0);
                setScoreColumnWidth(0);
                setPointsColumnWidth(0);
                setContainerWidth(75);
              }}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 py-1.5 px-3 rounded text-xs transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default NREMTAssessmentEditor;
